<template>
  <div>
    <div
      v-if="error"
      class="alert alert-danger d-flex align-items-center gap-2 m-3"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <div
      v-if="!statsLoading"
      class="wl-kpis"
    >
      <div class="wl-stat wl-stat--dark">
        <div class="wl-stat__head">
          <span class="wl-stat__title">{{ $t('components.infrastructure.kpis.gatewaysActive') }}</span>
          <i class="bi bi-hdd-network wl-stat__icon" />
        </div>
        <span class="wl-stat__value">{{ stats.gatewaysActive }}/{{ stats.gatewaysTotal }}</span>
        <span class="wl-stat__meta">{{ $t('components.infrastructure.kpis.gatewaysActiveMeta') }}</span>
      </div>

      <div class="wl-stat">
        <div class="wl-stat__head">
          <span class="wl-stat__title">{{ $t('components.infrastructure.kpis.anchorsActive') }}</span>
          <i
            class="bi bi-broadcast wl-stat__icon"
            style="color:#3f8f52"
          />
        </div>
        <span class="wl-stat__value">{{ stats.anchorsActive }}/{{ stats.anchorsTotal }}</span>
        <div
          class="wl-stat__accent"
          :style="{ width: stats.anchorsTotal ? `${(stats.anchorsActive / stats.anchorsTotal) * 100}%` : '0%', background: '#3f8f52' }"
        />
        <span class="wl-stat__meta">{{ $t('components.infrastructure.kpis.anchorsActiveMeta') }}</span>
      </div>
    </div>

    <div class="assets-grid-card">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-hdd-network" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.gateways.title') }}
            </div>
          </div>
        </div>
      </div>
      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap">
          <input
            v-model="gatewaySearch"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.gateways.searchPlaceholder')"
            @input="onGatewaySearchInput"
          >
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="gatewaysLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            ref="gatewaysGridRef"
            class="ni-grid"
            :data-source="gatewaysDataSource"
            :remote-operations="true"
            key-expr="gwId"
            :hover-state-enabled="true"
            :column-auto-width="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :column-hiding-enabled="isNarrowViewport"
            :word-wrap-enabled="false"
            :no-data-text="$t('components.infrastructure.gateways.empty')"
            @row-click="onGatewayRowClick"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="locationName"
              :caption="$t('components.infrastructure.gateways.columns.location')"
              :min-width="220"
              :allow-sorting="false"
              cell-template="loc-template"
            />
            <DxColumn
              data-field="gwId"
              :caption="$t('components.infrastructure.gateways.columns.gwId')"
              width="150"
              :allow-sorting="false"
              cell-template="gwid-template"
            />
            <DxColumn
              data-field="gatewayModel"
              :caption="$t('components.infrastructure.gateways.columns.model')"
              width="140"
              :allow-sorting="false"
            />
            <DxColumn
              data-field="sinkIds"
              :caption="$t('components.infrastructure.gateways.columns.sinks')"
              width="100"
              :allow-sorting="false"
              cell-template="sinks-template"
            />
            <DxColumn
              data-field="assetCount"
              :caption="$t('components.infrastructure.gateways.columns.assetCount')"
              width="125"
              alignment="center"
              :allow-sorting="false"
            />
            <DxColumn
              data-field="messageCount"
              :caption="$t('components.infrastructure.gateways.columns.messageCount')"
              width="125"
              alignment="center"
              :allow-sorting="false"
              cell-template="msgcount-template"
            />
            <DxColumn
              data-field="lastAssetSeen"
              :caption="$t('components.infrastructure.gateways.columns.lastAssetSeen')"
              width="165"
              :allow-sorting="false"
              cell-template="lastasset-template"
            />
            <DxColumn
              data-field="updatedAt"
              :caption="$t('components.infrastructure.gateways.columns.updatedAt')"
              width="250"
              :allow-sorting="false"
              cell-template="updated-template"
            />
            <DxColumn
              data-field="state"
              :caption="$t('components.infrastructure.gateways.columns.status')"
              width="120"
              alignment="center"
              :allow-sorting="false"
              cell-template="gwstatus-template"
            />
            <DxColumn
              :caption="$t('components.assetGroupsWithLocation.columns.actions')"
              width="100"
              :allow-filtering="false"
              :allow-sorting="false"
              cell-template="gw-actions-template"
            />

            <template #loc-template="{ data }">
              <span v-if="data.data.locationName">
                {{ data.data.locationName }}
                <span
                  v-if="data.data.locationCity"
                  class="text-muted small"
                > · {{ data.data.locationCity }}</span>
              </span>
              <span
                v-else
                class="text-muted"
              >{{ $t('components.infrastructure.gateways.unassigned') }}</span>
            </template>

            <template #gwid-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.gwId }}</span>
            </template>

            <template #sinks-template="{ data }">
              <span
                v-if="data.data.sinkIds && data.data.sinkIds.length"
                class="text-muted small font-monospace"
                :title="data.data.sinkIds.join(', ')"
              >{{ data.data.sinkIds.join(', ') }}</span>
              <span
                v-else-if="data.data.sinkCount"
                class="text-muted small font-monospace"
              >{{ data.data.sinkCount }}</span>
              <span
                v-else
                class="text-muted"
              >—</span>
            </template>

            <template #updated-template="{ data }">
              <span class="text-muted small font-monospace">{{ agoText(data.data.updatedAt) }}</span>
            </template>

            <template #msgcount-template="{ data }">
              <span class="text-muted small font-monospace">{{ Number(data.data.messageCount ?? 0).toLocaleString('pl-PL') }}</span>
            </template>

            <template #lastasset-template="{ data }">
              <span class="text-muted small font-monospace">{{ agoText(data.data.lastAssetSeen) }}</span>
            </template>

            <template #gwstatus-template="{ data }">
              <span
                class="wirepas-status-badge"
                :class="data.data.state === 'online' ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
              >
                <span
                  class="wirepas-status-dot"
                  :class="data.data.state === 'online' ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                />
                {{ data.data.state === 'online' ? $t('components.infrastructure.online') : $t('components.infrastructure.offline') }}
              </span>
            </template>

            <template #gw-actions-template="{ data }">
              <div
                v-if="data.data.assignedLocationId"
                class="wl-row-actions"
              >
                <button
                  type="button"
                  class="wl-row-action"
                  :title="$t('components.infrastructure.gateways.showLocation')"
                  @click.stop="$router.push(`/wirepas-locations/${data.data.assignedLocationId}/detail`)"
                >
                  <i class="bi bi-eye" />
                </button>
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>

    <div class="assets-grid-card ni-grid-card--spaced">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-broadcast" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.anchors.title') }}
            </div>
          </div>
        </div>
        <div class="assets-grid-controls">
          <button
            v-if="selectedGwId"
            type="button"
            class="assets-toolbar-btn"
            @click="clearGatewayFilter"
          >
            <span>{{ $t('components.infrastructure.anchors.filterLabel') }}: {{ shortGwId(selectedGwId) }}</span>
            <i class="bi bi-x-lg" />
          </button>
        </div>
      </div>
      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap">
          <input
            v-model="anchorSearch"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.anchors.searchPlaceholder')"
            @input="onAnchorSearchInput"
          >
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="anchorsLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            ref="anchorsGridRef"
            class="ni-grid"
            :data-source="anchorsDataSource"
            :remote-operations="true"
            key-expr="address"
            :hover-state-enabled="true"
            :column-auto-width="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :column-hiding-enabled="isNarrowViewport"
            :word-wrap-enabled="true"
            :no-data-text="selectedGwId ? $t('components.infrastructure.anchors.emptyFiltered') : $t('components.infrastructure.anchors.empty')"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="address"
              :caption="$t('components.infrastructure.anchors.columns.address')"
              :min-width="150"
              :allow-sorting="false"
              cell-template="addr-template"
            />
            <DxColumn
              data-field="gateways"
              :caption="$t('components.infrastructure.anchors.columns.gateway')"
              :min-width="200"
              :allow-sorting="false"
              cell-template="anchor-gw-template"
            />
            <DxColumn
              data-field="tagsHeard"
              :caption="$t('components.infrastructure.anchors.columns.tagsHeard')"
              :min-width="110"
              alignment="center"
              :allow-sorting="false"
              :hiding-priority="1"
            />
            <DxColumn
              data-field="lastRssiDbm"
              :caption="$t('components.infrastructure.anchors.columns.lastRssi')"
              :min-width="110"
              alignment="center"
              :allow-sorting="false"
              cell-template="rssi-template"
              :hiding-priority="2"
            />
            <DxColumn
              data-field="lastSeen"
              :caption="$t('components.infrastructure.anchors.columns.lastSeen')"
              :min-width="140"
              :allow-sorting="false"
              cell-template="anchor-updated-template"
            />
            <DxColumn
              data-field="active"
              :caption="$t('components.infrastructure.anchors.columns.status')"
              :min-width="110"
              alignment="center"
              :allow-sorting="false"
              cell-template="anchor-status-template"
            />

            <template #addr-template="{ data }">
              <span class="font-monospace small">{{ data.data.address }}</span>
            </template>

            <template #anchor-gw-template="{ data }">
              <span
                v-if="data.data.gateways && data.data.gateways.length"
                class="ni-anchor-gw-list"
              >
                <span
                  v-for="gw in data.data.gateways.slice(0, 2)"
                  :key="gw"
                  class="ni-anchor-gw"
                  :class="{ 'ni-anchor-gw--selected': selectedGwId === gw }"
                  @click.stop="onAnchorGwClick(gw)"
                >{{ gw }}</span>
                <span
                  v-if="data.data.gateways.length > 2"
                  class="text-muted small"
                  :title="data.data.gateways.slice(2).join(', ')"
                >+{{ data.data.gateways.length - 2 }}</span>
              </span>
              <span
                v-else
                class="text-muted"
              >—</span>
            </template>

            <template #rssi-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.lastRssiDbm != null ? `${data.data.lastRssiDbm} dBm` : '—' }}</span>
            </template>

            <template #anchor-updated-template="{ data }">
              <span class="text-muted small font-monospace">{{ agoText(data.data.lastSeen) }}</span>
            </template>

            <template #anchor-status-template="{ data }">
              <span
                class="wirepas-status-badge"
                :class="data.data.active ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
              >
                <span
                  class="wirepas-status-dot"
                  :class="data.data.active ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                />
                {{ data.data.active ? $t('components.infrastructure.anchors.status.active') : $t('components.infrastructure.anchors.status.silent') }}
              </span>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import CustomStore from 'devextreme/data/custom_store'
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxLoadPanel,
} from 'devextreme-vue/data-grid'
import { getWirepasGatewaysAdmin, getWirepasGatewaysStats, getWirepasAnchors, getWirepasAnchorsStats } from '@/crud/wirepas-location'
import { wirepasAgoTextFromIso, shortGwId as shortGwIdUtil } from '@/utils/wirepas'
import { useNarrowViewport } from '@/composables/useNarrowViewport'
import { useDebouncedGridRefresh } from '@/composables/useDebouncedGridRefresh'

export default defineComponent({
  name: 'WirepasNetworkGrid',
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxLoadPanel },
  props: {
    active: { type: Boolean as PropType<boolean>, default: false },
  },
  setup(props) {
    const { t } = useI18n()

    const error = ref<string | null>(null)
    const statsLoading = ref(true)
    const stats = ref({ gatewaysTotal: 0, gatewaysActive: 0, anchorsTotal: 0, anchorsActive: 0 })

    const gatewaysLoading = ref(true)
    const anchorsLoading = ref(true)
    const gatewaySearch = ref('')
    const anchorSearch = ref('')
    const selectedGwId = ref<string | null>(null)
    const gatewaysGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null)
    const anchorsGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null)

    const isNarrowViewport = useNarrowViewport()

    // Two independent CustomStores -- neither grid's page/filter affects the other.
    const gatewaysDataSource = new CustomStore({
      key: 'gwId',
      load: async (opts) => {
        gatewaysLoading.value = true
        try {
          const params: any = { skip: opts.skip ?? 0, take: opts.take ?? 10 }
          if (gatewaySearch.value.trim()) params.searchValue = gatewaySearch.value.trim()
          const res = await getWirepasGatewaysAdmin(params)
          return { data: res.data?.result ?? [], totalCount: res.data?.count ?? 0 }
        } catch (e: any) {
          error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
          throw e
        } finally {
          gatewaysLoading.value = false
        }
      },
    })

    const anchorsDataSource = new CustomStore({
      key: 'address',
      load: async (opts) => {
        anchorsLoading.value = true
        try {
          const params: any = { skip: opts.skip ?? 0, take: opts.take ?? 10 }
          if (anchorSearch.value.trim()) params.searchValue = anchorSearch.value.trim()
          if (selectedGwId.value) params.gwId = selectedGwId.value
          const res = await getWirepasAnchors(params)
          return { data: res.data?.result ?? [], totalCount: res.data?.count ?? 0 }
        } catch (e: any) {
          error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
          throw e
        } finally {
          anchorsLoading.value = false
        }
      },
    })

    const fetchStats = async () => {
      try {
        const [gwStatsRes, anStatsRes] = await Promise.all([getWirepasGatewaysStats(), getWirepasAnchorsStats()])
        stats.value = {
          gatewaysTotal: gwStatsRes.data?.total ?? 0,
          gatewaysActive: gwStatsRes.data?.active ?? 0,
          anchorsTotal: anStatsRes.data?.total ?? 0,
          anchorsActive: anStatsRes.data?.active ?? 0,
        }
      } catch {
        // KPI bar doesn't block the rest of the view
      } finally {
        statsLoading.value = false
      }
    }

    // Renders only after first activation -- all Infrastructure tabs mount together via v-show.
    const hasActivated = ref(props.active)

    // Fetches once per tab activation, no periodic auto-refresh.
    watch(() => props.active, (isActive) => {
      if (isActive) {
        hasActivated.value = true
        fetchStats()
      }
    }, { immediate: true })

    const onGatewaySearchInput = useDebouncedGridRefresh(gatewaysGridRef)
    const onAnchorSearchInput = useDebouncedGridRefresh(anchorsGridRef)

    // Anchors grid reloads whenever the gateway cross-filter changes.
    watch(selectedGwId, () => anchorsGridRef.value?.instance?.refresh())

    const onGatewayRowClick = (e: any) => {
      const gwId = e.data?.gwId
      if (!gwId) return
      selectedGwId.value = selectedGwId.value === gwId ? null : gwId
    }
    const onAnchorGwClick = (gwId: string) => {
      selectedGwId.value = selectedGwId.value === gwId ? null : gwId
    }
    const clearGatewayFilter = () => {
      selectedGwId.value = null
    }

    const shortGwId = shortGwIdUtil
    const agoText = (iso: string | null) => wirepasAgoTextFromIso(iso, t)

    return {
      error,
      statsLoading,
      stats,
      hasActivated,
      gatewaysLoading,
      anchorsLoading,
      gatewaySearch,
      anchorSearch,
      selectedGwId,
      gatewaysGridRef,
      anchorsGridRef,
      gatewaysDataSource,
      anchorsDataSource,
      isNarrowViewport,
      onGatewaySearchInput,
      onAnchorSearchInput,
      onGatewayRowClick,
      onAnchorGwClick,
      clearGatewayFilter,
      shortGwId,
      agoText,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';

.ni-anchor-gw-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.ni-anchor-gw {
  font-family: monospace;
  font-size: 12px;
  color: var(--primary-color);
  cursor: pointer;
  text-decoration: underline dotted;

  &--selected {
    font-weight: 700;
  }
}
</style>
